import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Copy, FileText, GraduationCap, Loader2, Plus, Trash2, TriangleAlert } from 'lucide-react';
import {
  DESCRICAO_MODELO_DE_DOCUMENTO,
  MODELOS_DE_DOCUMENTO,
  ROTULO_MODELO_DE_DOCUMENTO,
  perfisDoModelo,
} from '@miolos/core';
import type { ModeloDeDocumento } from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Campo } from '@/components/ui/label';
import { api, ErroApi, type DocumentoSalvo } from '@/lib/api';
import { CartaoOpcao } from '@/components/CartaoOpcao';
import { PublicarNaBiblioteca } from '@/documento/PublicarNaBiblioteca';
import { Grade } from '@/wizard/ui';

/**
 * Meus documentos: lista dos trabalhos e artigos, e o começo de um novo.
 *
 * A tela é separada da lista de miolos de propósito — são dois produtos: ali são
 * agendas para imprimir e vender, aqui são trabalhos acadêmicos para apresentar.
 * O acesso, porém, é o mesmo cuidado: quem não tem o editor liberado vê o convite
 * para pedir a liberação, não um erro.
 */
export function Documentos() {
  const navegar = useNavigate();
  const [documentos, setDocumentos] = useState<DocumentoSalvo[] | null>(null);
  const [erro, setErro] = useState('');
  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState('');
  const [modelo, setModelo] = useState<ModeloDeDocumento>('TRABALHO_ACADEMICO');
  /**
   * O padrão de formatação: a norma ou o manual da instituição.
   *
   * Nasce na ABNT porque é o que serve a quem não tem manual próprio — e trocar de
   * modelo filtra a lista (o padrão da Fatec não se aplica a artigo, por exemplo).
   */
  const [perfil, setPerfil] = useState('ABNT');
  const perfis = perfisDoModelo(modelo);
  const perfilValido = perfis.some((item) => item.id === perfil) ? perfil : (perfis[0]?.id ?? 'ABNT');
  const [ocupado, setOcupado] = useState('');

  const carregar = useCallback(async () => {
    try {
      const { documentos: lista } = await api.listarDocumentos();
      setDocumentos(lista);
      setErro('');
    } catch (causa) {
      setDocumentos([]);
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar seus documentos.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function criar() {
    if (nome.trim().length < 2) {
      setErro('Dê um nome ao documento — pode ser o título provisório.');
      return;
    }

    setOcupado('criar');
    try {
      const { documento } = await api.criarDocumento(nome.trim(), modelo, perfilValido);
      navegar(`/documentos/${documento.id}`);
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível criar o documento.');
      setOcupado('');
    }
  }

  async function duplicar(id: string) {
    setOcupado(id);
    try {
      await api.duplicarDocumento(id);
      await carregar();
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível duplicar.');
    } finally {
      setOcupado('');
    }
  }

  async function apagar(id: string, titulo: string) {
    if (!window.confirm(`Apagar "${titulo}"? O documento não volta.`)) return;
    setOcupado(id);
    try {
      await api.apagarDocumento(id);
      await carregar();
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível apagar.');
    } finally {
      setOcupado('');
    }
  }

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <span className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary">
          <GraduationCap className="size-5" />
        </span>
        <h1 className="text-2xl font-semibold tracking-tight">Documentos acadêmicos</h1>
        <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Escreva o trabalho ou o artigo aqui e receba a formatação da ABNT pronta: margens e entrelinha
          da norma, seções numeradas, resumo com palavras-chave, figuras com legenda e fonte, e as
          referências em ordem. Você escolhe quais peças entram — capa, folha de rosto, sumário, listas —
          e pode enviar imagens para a capa e para o cabeçalho das páginas.
        </p>
      </header>

      {erro ? (
        <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          {erro}
        </p>
      ) : null}

      {/* Começar um documento */}
      <Card>
        <CardHeader>
          <CardTitle>Começar um documento</CardTitle>
          <CardDescription>
            Escolha o formato: o trabalho acadêmico sai com os elementos pré e pós-textuais (capa, folha
            de rosto, resumo, sumário, referências); o artigo sai com título, autoria, resumo e abstract e
            as seções corridas.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Grade colunas={2}>
            {MODELOS_DE_DOCUMENTO.map((item) => (
              <CartaoOpcao
                key={item}
                selecionado={modelo === item}
                titulo={ROTULO_MODELO_DE_DOCUMENTO[item]}
                descricao={DESCRICAO_MODELO_DE_DOCUMENTO[item]}
                aoSelecionar={() => setModelo(item)}
              />
            ))}
          </Grade>

          {/*
            O padrão da instituição: o manual de cada faculdade por cima da norma.
            Só as que têm manual próprio aparecem aqui, e a lista é filtrada pelo
            modelo — o padrão da Fatec vale para trabalho acadêmico, não para artigo.
          */}
          {perfis.length > 1 ? (
            <div className="space-y-2">
              <Campo
                rotulo="Padrão de formatação"
                dica="O manual da sua instituição, quando ele diverge da ABNT. Dá para trocar depois, sem perder o texto."
              >
                <select
                  value={perfilValido}
                  onChange={(evento) => setPerfil(evento.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
                >
                  {perfis.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.sigla} — {item.nome}
                    </option>
                  ))}
                </select>
              </Campo>
              {(() => {
                const escolhido = perfis.find((item) => item.id === perfilValido);
                return escolhido ? (
                  <p className="text-xs leading-relaxed text-muted-foreground">{escolhido.descricao}</p>
                ) : null;
              })()}
            </div>
          ) : null}

          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[240px] flex-1">
              <Campo rotulo="Nome do documento" dica="É só para você achar depois — o título do trabalho é outro campo.">
                <input
                  value={nome}
                  onChange={(evento) => setNome(evento.target.value)}
                  placeholder="TCC sobre alfabetização"
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
                />
              </Campo>
            </div>
            <Button onClick={() => void criar()} disabled={ocupado === 'criar'}>
              {ocupado === 'criar' ? <Loader2 className="animate-spin" /> : <Plus />}
              Criar documento
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold tracking-tight">Meus documentos</h2>

        {documentos === null ? (
          <div className="flex min-h-[20vh] items-center justify-center gap-3 text-muted-foreground">
            <Loader2 className="size-5 animate-spin" />
            <span className="text-sm">Carregando...</span>
          </div>
        ) : documentos.length === 0 ? (
          <Card>
            <CardHeader>
              <span className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                <FileText className="size-5" />
              </span>
              <CardTitle className="pt-1">Nenhum documento por aqui ainda</CardTitle>
              <CardDescription>
                Comece pelo formato acima: o documento nasce com o esqueleto de seções que a banca espera
                (introdução, referencial, metodologia, resultados, considerações finais) e você escreve
                por cima.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <ul className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {documentos.map((documento) => (
              <li key={documento.id}>
                <Card className="flex h-full flex-col">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <CardTitle className="leading-tight">{documento.nome}</CardTitle>
                      <Badge tom="principal">
                        {documento.modelo === 'ARTIGO_CIENTIFICO' ? 'Artigo' : 'Trabalho'}
                      </Badge>
                    </div>
                    <CardDescription>
                      {documento.resumo?.titulo ?? 'Sem título preenchido'}
                      {documento.resumo?.autores ? ` · ${documento.resumo.autores}` : ''}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="mt-auto space-y-3">
                    <p className="text-xs text-muted-foreground">
                      {documento.resumo?.secoes ?? 0} seção(ões) · {documento.resumo?.figuras ?? 0}{' '}
                      figura(s) · {documento.resumo?.referencias ?? 0} referência(s) ·{' '}
                      {documento.resumo?.itens ?? 0} item(ns)
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button asChild tamanho="pequeno">
                        <Link to={`/documentos/${documento.id}`}>Escrever</Link>
                      </Button>
                      <Button
                        variant="contorno"
                        tamanho="pequeno"
                        onClick={() => void duplicar(documento.id)}
                        disabled={ocupado === documento.id}
                      >
                        <Copy />
                        Duplicar
                      </Button>
                      <PublicarNaBiblioteca documentoId={documento.id} aoPublicar={carregar} />
                      <Button
                        variant="fantasma"
                        tamanho="pequeno"
                        onClick={() => void apagar(documento.id, documento.nome)}
                        disabled={ocupado === documento.id}
                      >
                        <Trash2 />
                        Apagar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
